// =============================================================================
// WS-ICON-01 — the navigation glyph set, shared by every app's chrome.
//
// PROMOTED FROM apps/platform-admin/src/shell/NavIcons.tsx, unchanged in
// appearance. It lives here because two apps drawing their own "Units" glyph
// is how the console and the main app drift into looking like different
// products — and because the console's set was already the de-facto vocabulary.
//
// Hand-written SVG rather than an icon-set import: these paths ARE the locked
// mockup's, and substituting the nearest Material icon would be a redraw of a
// locked asset. They are decorative — the nav item's text is its accessible
// name — so each is `aria-hidden`.
//
// `stroke="currentColor"` on the shared `Glyph` is load-bearing and not
// decoration: with `fill="none"` and SVG's default `stroke: none`, every path
// paints NOTHING. That shipped, invisibly, until 2026-08-30 — see the note on
// Glyph. It is also what lets a caller colour a glyph with `color`, which is
// how the console styles its active leaf.
//
// WHAT DOES NOT LIVE HERE: the path -> glyph mapping. That is app-specific
// composition (packages/ui/src/index.ts: "App-specific composition lives in
// apps/*"), and each app's nav has different entries.
// =============================================================================

import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

function Glyph({ children, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={16}
      height={16}
      fill="none"
      // THE GLYPHS WERE INVISIBLE WITHOUT THIS. `fill="none"` with no `stroke`
      // means SVG's default `stroke: none` — every path painted nothing, so
      // the whole sidebar showed blank space where its six icons should be.
      // `strokeWidth` was set, which made it look deliberate.
      //
      // `currentColor` is what the shell already assumes: it styles the active
      // leaf with `"& svg": { color: "primary.main" }`, and `color` reaches a
      // stroke ONLY through currentColor. ConsoleGlyph and ExternalGlyph in
      // AdminShell.tsx both set it and both render; this file was the only
      // one that did not.
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

export function DashboardIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </Glyph>
  );
}

export function UnitsIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
    </Glyph>
  );
}

/** AFIS-LOG-03 — the log registry: a sheet with three lines and a corner fold. */
export function LogsIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M7 3h7l5 5v13H7z" />
      <path d="M14 3v5h5M10 12h6M10 15.5h6M10 19h4" />
    </Glyph>
  );
}

/**
 * AFIS-GS-04 — a ground station: a laptop, because that is literally what one
 * is. The GCS runs on a pilot's Windows PC, and the whole point of the
 * identity is that it belongs to the MACHINE rather than to a unit — so the
 * glyph is a machine and not another variation on the drone-unit circle.
 */
export function GroundStationsIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <rect x="4" y="5" width="16" height="10" rx="1.5" />
      <path d="M2 19h20M9.5 15l-.7 2.2h6.4L14.5 15" />
    </Glyph>
  );
}

export function FirmwareIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <rect x="5" y="5" width="14" height="14" rx="2" />
      <path d="M9 2v3M15 2v3M9 19v3M15 19v3M2 9h3M2 15h3M19 9h3M19 15h3" />
    </Glyph>
  );
}

export function PresetsIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M4 7h16M4 12h16M4 17h16" />
      <circle cx="9" cy="7" r="1.8" />
      <circle cx="15" cy="12" r="1.8" />
      <circle cx="7" cy="17" r="1.8" />
    </Glyph>
  );
}

export function TenantLinksIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M9 15l6-6M8 12a4 4 0 0 1 0-6l1-1a4 4 0 0 1 6 6" />
      <path d="M16 12a4 4 0 0 1 0 6l-1 1a4 4 0 0 1-6-6" />
    </Glyph>
  );
}

/**
 * ADM-AUDIT-01. Not in the locked mockup — that predates this page — so it is
 * drawn in the same hand: one `Glyph`, 24-unit box, 1.7 stroke, no fill.
 *
 * A ledger with a folded corner and two ruled lines. Deliberately NOT a clock:
 * `UnitsIcon` already owns the circle, and two round glyphs in one 16px rail
 * read as the same icon at a glance.
 */
export function AuditLogIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M6 3h8l4 4v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" />
      <path d="M14 3v4h4" />
      <path d="M8 13h8M8 17h5" />
    </Glyph>
  );
}

/**
 * ADM-USR-01 — the platform-admin directory.
 *
 * A person with a key. Two glyphs already own the obvious alternatives and
 * both would read as the same icon at 16px in one rail: `UnitsIcon` has the
 * bare circle, `TenantLinksIcon` has the linked pair. A single figure with a
 * key says what this leaf is — who holds the keys to the platform — and is
 * distinguishable from both at a glance, which is the only size that matters
 * here.
 */
export function PlatformAdminsIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="9" cy="7.5" r="3" />
      <path d="M3.5 20a5.5 5.5 0 0 1 11 0" />
      <circle cx="17.5" cy="13" r="2" />
      <path d="M19 14.5l2.5 2.5M20.2 15.7l-1 1" />
    </Glyph>
  );
}

/**
 * SYNC-01 — the per-tenant entitlements (what a tenant has been sold: the
 * workflows and the quality checks, each with its negotiated price). A price
 * tag: `TenantLinksIcon` owns the linked pair and `PlatformAdminsIcon` the key,
 * so a tag reads as "what is sold" and stays distinct from both at 16px.
 */
export function EntitlementsIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M3.5 12.2V4.5a1 1 0 0 1 1-1h7.7l8.3 8.3a1.4 1.4 0 0 1 0 2l-6.2 6.2a1.4 1.4 0 0 1-2 0z" />
      <circle cx="8" cy="8" r="1.5" />
    </Glyph>
  );
}
